// Computes pixel offset for the current time indicator.
import useCurrentTime from '@/features/tasks/hooks/status/use-task-current-time';
import { CALENDAR_START_HOUR, CALENDAR_END_HOUR } from '@/config/constants';
import { TIME_SLOT_MINS } from '@/config/constants';

/**
 * Hook to calculate the vertical position of the current time indicator in the calendar.
 * The indicator is hidden outside of the active hours.
 * @param {number} fifteenMinuteSlotHeight - The height of a 15-minute slot in pixels.
 * @param {number} startHour - The hour at which the calendar view starts.
 * @param {number} endHour - The hour at which the calendar view ends.
 * @returns {number} The calculated 'top' position in pixels, or -1 if the current time is outside the visible range.
 */
const useCurrentTimePosition = (
  fifteenMinuteSlotHeight,
  startHour = CALENDAR_START_HOUR,
  endHour = CALENDAR_END_HOUR,
) => {
  const currentTime = useCurrentTime();
  const currentHour = currentTime.getHours();

  // Hide the indicator if the current time is outside the active hours
  if (currentHour < startHour || currentHour >= endHour) {
    return -1;
  }

  if (fifteenMinuteSlotHeight === 0) {
    return 0; // Avoid division by zero before the height is calculated
  }

  const minutesFromStart =
    (currentHour - startHour) * 60 + currentTime.getMinutes();
  const position =
    minutesFromStart * (fifteenMinuteSlotHeight / TIME_SLOT_MINS);

  return position;
};

export default useCurrentTimePosition;
